import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  CardBody, 
  CardHeader, 
  Form, 
  FormGroup, 
  Label, 
  Input, 
  Button, 
  Alert,
  InputGroup
} from 'reactstrap';
import { AccountClient, UpdateAccountDto, SetApiKeyDto } from '../api/api-client';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../hooks/useAccount';


export const AccountSettings: React.FC = () => {
  const { refreshUser } = useAuth();
  const { data: account, isLoading: loading, refetch } = useAccount();
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  
  // Form state
  const [accountName, setAccountName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [claudeApiKey, setClaudeApiKey] = useState('');
  const [openAIApiKey, setOpenAIApiKey] = useState('');
  const [showClaudeKey, setShowClaudeKey] = useState(false);
  const [showOpenAIKey, setShowOpenAIKey] = useState(false);

  // Populate form when account data loads
  useEffect(() => {
    if (account) {
      setAccountName(account.name || '');
      setCompanyName(account.companyName || '');
    }
  }, [account]);

  const handleSaveAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      const client = new AccountClient();
      
      const updateDto: UpdateAccountDto = {
        name: accountName,
        companyName: companyName || undefined
      };
      
      await client.updateAccount(updateDto);
      
      setSuccessMessage('Account settings updated successfully');
      setTimeout(() => setSuccessMessage(''), 5000);
      
      // Reload account data using TanStack Query
      await refetch();
    } finally {
      setSaving(false);
    }
  };

  const handleSaveApiKey = async (keyType: 'claude' | 'openai', apiKey: string) => {
    if (!apiKey.trim()) 
      throw new Error('API key cannot be empty');

    try {
      setSaving(true);
      const client = new AccountClient();
      
      const setKeyDto: SetApiKeyDto = {
        apiKey: apiKey.trim()
      };
      
      await client.setApiKey(keyType, setKeyDto);
      
      setSuccessMessage(`${keyType === 'claude' ? 'Claude' : 'OpenAI'} API key updated successfully`);
      setTimeout(() => setSuccessMessage(''), 5000);
      
      // Clear the input and reload account data
      if (keyType === 'claude') {
        setClaudeApiKey('');
      } else {
        setOpenAIApiKey('');
      }
      
      await refetch();
      await refreshUser();
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveApiKey = async (keyType: 'claude' | 'openai') => {
    try {
      setSaving(true);
      const client = new AccountClient();
      
      await client.removeApiKey(keyType);
      
      setSuccessMessage(`${keyType === 'claude' ? 'Claude' : 'OpenAI'} API key removed successfully`);
      setTimeout(() => setSuccessMessage(''), 5000);
      
      await refetch();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading account settings...</p>
        </div>
      </Container>
    );
  }

  if (!account) {
    return (
      <Container className="mt-4">
        <Alert color="danger">
          Failed to load account information. Please try refreshing the page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row>
        <Col md={8} lg={6} className="mx-auto">
          <h2 className="mb-4">Account Settings</h2>
          
          {successMessage && (
            <Alert color="success" className="mb-4">
              {successMessage}
            </Alert>
          )}

          {/* Account Information */}
          <Card className="mb-4">
            <CardHeader>
              <h5 className="mb-0">Account Information</h5>
            </CardHeader>
            <CardBody>
              <Form onSubmit={handleSaveAccount}>
                <FormGroup>
                  <Label for="accountName">Account Name</Label>
                  <Input
                    type="text"
                    id="accountName"
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value)}
                    required
                    disabled={saving}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label for="companyName">Company Name (Optional)</Label>
                  <Input
                    type="text"
                    id="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    disabled={saving}
                  />
                </FormGroup>

                <div className="d-flex justify-content-between align-items-center text-muted small mb-3">
                  <span>Account ID: {account.accountId}</span>
                  <span>Plan: {account.subscriptionLevel}</span>
                </div>
                
                <Button 
                  type="submit" 
                  color="primary" 
                  disabled={saving}
                  className="w-100"
                >
                  {saving ? 'Saving...' : 'Save Account Settings'}
                </Button>
              </Form>
            </CardBody>
          </Card>

          {/* API Keys */}
          <Card className="mb-4">
            <CardHeader>
              <h5 className="mb-0">API Keys</h5>
            </CardHeader>
            <CardBody>
              {/* Claude API Key */}
              <FormGroup>
                {!account.hasClaudeApiKey && <Alert color="danger" className="mb-3">Please set up an a Claude API key for your account - required to continue.</Alert>}
                <Label for="claudeApiKey">
                  Claude API Key 
                  {account.hasClaudeApiKey ? (
                    <span className="badge bg-success ms-2">Configured</span>
                  ) : (
                    <span className="badge bg-warning ms-2">Not Set</span>
                  )}
                </Label>
                <InputGroup>
                  <Input
                    type={showClaudeKey ? 'text' : 'password'}
                    id="claudeApiKey"
                    value={claudeApiKey}
                    onChange={(e) => setClaudeApiKey(e.target.value)}
                    placeholder={account.hasClaudeApiKey ? "Enter new Claude API key to update" : "Enter your Claude API key"}
                    disabled={saving}
                  />
                  <Button
                    type="button"
                    color="outline-secondary"
                    onClick={() => setShowClaudeKey(!showClaudeKey)}
                    disabled={saving}
                  >
                    {showClaudeKey ? '👁️' : '👁️‍🗨️'}
                  </Button>
                </InputGroup>
                <div className="mt-2">
                  <Button
                    size="sm"
                    color="primary"
                    onClick={() => handleSaveApiKey('claude', claudeApiKey)}
                    disabled={saving || !claudeApiKey.trim()}
                    className="me-2"
                  >
                    {account.hasClaudeApiKey ? 'Update' : 'Set'} Key
                  </Button>
                  {account.hasClaudeApiKey && (
                    <Button
                      size="sm"
                      color="outline-danger"
                      onClick={() => handleRemoveApiKey('claude')}
                      disabled={saving}
                    >
                      Remove Key
                    </Button>
                  )}
                </div>
              </FormGroup>

              {/* OpenAI API Key */}
              <FormGroup>
                <Label for="openAIApiKey">
                  OpenAI API Key 
                  {account.hasOpenAIApiKey ? (
                    <span className="badge bg-success ms-2">Configured</span>
                  ) : (
                    <span className="badge bg-warning ms-2">Not Set</span>
                  )}
                </Label>
                <InputGroup>
                  <Input
                    type={showOpenAIKey ? 'text' : 'password'}
                    id="openAIApiKey"
                    value={openAIApiKey}
                    onChange={(e) => setOpenAIApiKey(e.target.value)}
                    placeholder={account.hasOpenAIApiKey ? "Enter new OpenAI API key to update" : "Enter your OpenAI API key"}
                    disabled={saving}
                  />
                  <Button
                    type="button"
                    color="outline-secondary"
                    onClick={() => setShowOpenAIKey(!showOpenAIKey)}
                    disabled={saving}
                  >
                    {showOpenAIKey ? '👁️' : '👁️‍🗨️'}
                  </Button>
                </InputGroup>
                <div className="mt-2">
                  <Button
                    size="sm"
                    color="primary"
                    onClick={() => handleSaveApiKey('openai', openAIApiKey)}
                    disabled={saving || !openAIApiKey.trim()}
                    className="me-2"
                  >
                    {account.hasOpenAIApiKey ? 'Update' : 'Set'} Key
                  </Button>
                  {account.hasOpenAIApiKey && (
                    <Button
                      size="sm"
                      color="outline-danger"
                      onClick={() => handleRemoveApiKey('openai')}
                      disabled={saving}
                    >
                      Remove Key
                    </Button>
                  )}
                </div>
              </FormGroup>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};